import { useEditor, Element, ROOT_NODE, type NodeId } from '@craftjs/core';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectPanelDragging,
  selectPageWidth,
  selectPreviewModeEnabled,
  selectIsFullscreenPreview,
} from '@/store/system/selectors';
import { setViewportBreakpoint } from '@/store/system/systemSlice';
import { getBreakpointFromWidth } from '@builder4/shared/utils/breakpointUtils';
import type { Breakpoint } from '@builder4/shared/types/layout';

import {
  selectScale,
  selectTranslateX,
  selectTranslateY,
  selectIsPanningMode,
} from '@/store/zoom/selectors';

import { useEffect, useRef, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import CraftRenderer from '@builder4/shared/components/CraftRenderer';
import { type RenderContextType } from '@builder4/shared/context/RenderContext';
import { useFonts } from '@/context/FontProvider';
import { useIcons } from '@/context/IconProvider';
import { useContainerHelper } from '@/hooks/useContainerHelper';
import { canDeleteNode } from '@/utils/canDeleteNode';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import EditorPage from '@/components/userComponents/Page';
import { CanvasErrorFallback } from '@/components/ErrorBoundary';
import * as Sentry from '@sentry/react';

import type { moveEndFuncType, saveJsonCbType } from '@/pages/editor/Editor';
import { useNormalizeHelper } from '@/hooks/useNormalizeNodes';
import { selectActiveGlobalStyle } from '@/store/globalStyles/selectors';
import { selectCurrentPage, selectCurrentPageId } from '@/store/pages/selectors';
import { useCraftSerialization } from '@/hooks/useCraftSerialization';
import { updatePageById } from '@/store/pages/thunks';
import { canDuplicateNode } from '@/utils/helperFunc';
import { useMixpanel } from '@/hooks/useMixpanel';
import { EVENT, SOURCE, PROPERTY } from '@/analytics/mixpanel';
import { useInspirationPages } from '@/hooks/useInspirationPages';
import { usePreviewAnimations } from '@/hooks/usePreviewAnimations';
import { ContextSwitcher } from '@/components/canvas/ContextSwitcher/ContextSwitcher';

export default function BuilderCanvas({
  onFrameRef,
  onNormalizeNode,
  onRegisterSaveJson,
}: {
  onFrameRef: (ref: HTMLDivElement | null) => void;
  onNormalizeNode: (onMoveEndHandler: moveEndFuncType) => void;
  onRegisterSaveJson: (saveJsonCb: saveJsonCbType) => void;
}) {
  const { removeNode, duplicateNode, toggleLock, toggleVisibility, copyNode, pasteNode } =
    useCraftHelper();
  const { reAdustFillableObjectSpace } = useContainerHelper();
  const { saveCraftJson } = useCraftSerialization();
  const handleNormalizeNode = useNormalizeHelper();
  const dispatch = useAppDispatch();
  const currentPageId = useAppSelector(selectCurrentPageId);
  const track = useMixpanel();

  const pageContainerRef = useRef<HTMLDivElement>(null);
  const pageWidth = useAppSelector(selectPageWidth);
  const isFullscreenPreview = useAppSelector(selectIsFullscreenPreview);
  const canvasWidth = isFullscreenPreview ? '100%' : pageWidth;
  const lastViewportBreakpoint = useRef<Breakpoint | null>(null); // WHY: Track the last breakpoint to avoid unnecessary re-renders when fullscreen viewpoint resizes

  useEffect(() => {
    if (!isFullscreenPreview) return;

    const update = () => {
      const newBreakpoint = getBreakpointFromWidth(window.innerWidth);
      if (newBreakpoint === lastViewportBreakpoint.current) return;
      lastViewportBreakpoint.current = newBreakpoint;
      dispatch(setViewportBreakpoint(newBreakpoint));
    };
    const visibilityListener = () => {
      if (document.visibilityState === 'visible') update();
    };
    update();
    window.addEventListener('resize', update);
    document.addEventListener('visibilitychange', visibilityListener);
    return () => {
      window.removeEventListener('resize', update);
      document.removeEventListener('visibilitychange', visibilityListener);
    };
  }, [isFullscreenPreview, dispatch]);
  const scale = useAppSelector(selectScale);
  const translateX = useAppSelector(selectTranslateX);
  const translateY = useAppSelector(selectTranslateY);
  const previewMode = useAppSelector(selectPreviewModeEnabled);
  usePreviewAnimations(previewMode);
  const isPanningMode = useAppSelector(selectIsPanningMode);
  const panelDragging = useAppSelector(selectPanelDragging);
  const currentPage = useAppSelector(selectCurrentPage);
  const activeGlobalStyle = useAppSelector(selectActiveGlobalStyle);

  const { registerEditorDeserializer } = useInspirationPages();

  const { actions, query, selectedNodeId } = useEditor((_, query) => ({
    selectedNodeId: query.getEvent('selected').first() as NodeId | null | undefined,
  }));

  useEffect(() => {
    registerEditorDeserializer((json: string) => {
      actions.deserialize(json);
    });
  }, [actions, registerEditorDeserializer]);

  const handleSelectNode = (e: MouseEvent) => {
    if (e.target instanceof HTMLElement && e.target.classList.contains('page-container')) {
      actions.selectNode(ROOT_NODE);
    }
  };

  const handleDeleteNode = useCallback(() => {
    if (!selectedNodeId) {
      console.warn('Delete action is disabled outside Craft or without a valid node ID.');
      return;
    }
    if (!canDeleteNode(query, selectedNodeId)) return;
    removeNode(selectedNodeId);
  }, [selectedNodeId, query, removeNode]);

  const handleDuplicate = useCallback(() => {
    if (!selectedNodeId) {
      console.warn('Duplicate action is disabled outside Craft or without a valid node ID.');
      return;
    }
    const node = query.node(selectedNodeId).get();
    if (!node || !canDuplicateNode(node)) return;

    const newNodeId = duplicateNode(selectedNodeId);
    if (!newNodeId) return;
    const parentId = node.data.parent as string;
    (['desktop', 'tablet', 'mobile'] as const).forEach((bp) => {
      reAdustFillableObjectSpace(parentId, newNodeId, bp);
    });
  }, [selectedNodeId, query, duplicateNode, reAdustFillableObjectSpace]);

  const handleCopyNode = useCallback(() => {
    if (!selectedNodeId) {
      console.warn('Copy action is disabled without a valid node ID.');
      return;
    }
    const node = query.node(selectedNodeId).get();
    if (!node || !canDuplicateNode(node)) return;
    copyNode(selectedNodeId);
  }, [selectedNodeId, query, copyNode]);

  const handlePasteNode = useCallback(() => {
    if (!selectedNodeId) {
      console.warn('Paste action is disabled outside Craft or without a valid node ID.');
      return;
    }
    pasteNode(selectedNodeId);
  }, [selectedNodeId, pasteNode]);

  const handleToggleNodeVisibility = useCallback(() => {
    if (!selectedNodeId) {
      console.warn('Visibility toggle is disabled outside Craft or without a valid node ID.');
      return;
    }
    toggleVisibility(selectedNodeId);
  }, [selectedNodeId, toggleVisibility]);

  const handleToggleNodeLock = useCallback(() => {
    if (!selectedNodeId) {
      console.warn('Lock toggle is disabled outside Craft or without a valid node ID.');
      return;
    }
    toggleLock(selectedNodeId);
  }, [selectedNodeId, toggleLock]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Use e.code for keyboard shortcuts to work regardless of keyboard layout
      const isShortcut = (code: string, withShift = false) =>
        (e.ctrlKey || e.metaKey) && e.code === code && e.shiftKey === withShift;

      // Allow undo/redo from INPUT/TEXTAREA only when inside a Craft-backed
      // inspector (marked with data-craft-history). Other inputs (SEO, scripts,
      // dialogs) keep native undo behaviour.
      // ContentEditable is handled by Slate's own undo, so block everything there.
      if (e.target instanceof HTMLElement && e.target.isContentEditable) {
        return;
      }

      const targetIsInput =
        e.target instanceof HTMLElement &&
        (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA');

      if (targetIsInput && !(e.target as HTMLElement).closest('[data-craft-history]')) {
        return;
      }

      if (previewMode) return;

      if (isShortcut('KeyZ')) {
        e.preventDefault();
        track(EVENT.UNDO_USED, {
          [PROPERTY.SOURCE]: SOURCE.KEYBOARD,
        });
        return actions.history.undo();
      }
      if (isShortcut('KeyZ', true)) {
        e.preventDefault();
        track(EVENT.REDO_USED, {
          [PROPERTY.SOURCE]: SOURCE.KEYBOARD,
        });
        return actions.history.redo();
      }

      // Block remaining shortcuts when focus is on input elements
      if (targetIsInput) {
        return;
      }

      // Use e.code for layout-independent shortcuts
      const shortcuts: Record<string, () => void> = {
        Backspace: handleDeleteNode,
        Delete: handleDeleteNode,
        KeyD: () => isShortcut('KeyD') && handleDuplicate(),
        KeyC: () => isShortcut('KeyC') && handleCopyNode(),
        KeyV: () => isShortcut('KeyV') && handlePasteNode(),
        KeyH: () => isShortcut('KeyH') && handleToggleNodeVisibility(),
        KeyL: () => isShortcut('KeyL') && handleToggleNodeLock(),
      };

      const action = shortcuts[e.code];
      if (action) {
        e.preventDefault();
        action();
      }
    },
    [
      actions,
      query,
      selectedNodeId,
      previewMode,
      handleDeleteNode,
      handleDuplicate,
      handleCopyNode,
      handlePasteNode,
      handleToggleNodeVisibility,
      handleToggleNodeLock,
      track,
    ],
  );

  // Keyboard shortcuts event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Reset scroll position when exiting preview mode
  useEffect(() => {
    if (!previewMode && pageContainerRef.current) {
      pageContainerRef.current.scrollTop = 0;
    }
  }, [previewMode]);

  // Mouse click handler for page container
  useEffect(() => {
    const el = pageContainerRef.current;
    if (!el) return;
    el.addEventListener('mousedown', handleSelectNode);
    return () => {
      el.removeEventListener('mousedown', handleSelectNode);
    };
  }, [handleSelectNode, actions]);

  const handleSaveJson = () => {
    const nodes = saveCraftJson();
    const body = { craft_json: JSON.parse(nodes) };
    if (currentPageId) {
      dispatch(updatePageById({ pageId: currentPageId, body }));
    }
  };

  useEffect(() => {
    onRegisterSaveJson(handleSaveJson);
  }, [onRegisterSaveJson, handleSaveJson]);

  useEffect(() => {
    onNormalizeNode(handleNormalizeNode);
  }, [onNormalizeNode, handleNormalizeNode]);

  const { fontManager } = useFonts();
  const { iconManager } = useIcons();

  const context: RenderContextType = useMemo(
    () => ({
      environment: previewMode ? 'preview' : 'builder',
      isEditable: !previewMode,
      fontManager: fontManager ?? undefined,
      iconManager: iconManager ?? undefined,
      styles: activeGlobalStyle,
    }),
    [previewMode, fontManager, iconManager, activeGlobalStyle],
  );

  return (
    <div
      ref={(el) => {
        pageContainerRef.current = el;
        onFrameRef(el);
      }}
      id="page-container"
      className={clsx('page-container relative h-[calc(100vh-52px)] flex-grow-1', {
        'flex justify-center overflow-y-auto': previewMode,
        'py-15': previewMode && !isFullscreenPreview,
        'overflow-hidden': !previewMode,
      })}
      tabIndex={-1} // Make div focusable for keyboard events
    >
      <div
        style={
          previewMode
            ? ({
                width: canvasWidth,
                '--canvas-scale': '1',
              } as React.CSSProperties)
            : ({
                width: canvasWidth,
                transform: `translate(${translateX}px,${translateY}px) scale(${scale})`,
                transformOrigin: 'top left',
                '--canvas-scale': String(scale),
              } as React.CSSProperties)
        }
        className={clsx('craftjs-renderer relative flex justify-center', {
          'h-[min-content]': previewMode,
        })}
      >
        {!previewMode && !isPanningMode && <ContextSwitcher />}
        <Sentry.ErrorBoundary fallback={<CanvasErrorFallback />} showDialog={false}>
          <CraftRenderer
            craftJson={currentPage ? currentPage.craftJson.forRender() : ''}
            context={context}
          >
            <Element canvas is={EditorPage}></Element>
          </CraftRenderer>
        </Sentry.ErrorBoundary>
      </div>
      {panelDragging ||
        (isPanningMode && (
          <div className="absolute top-0 left-0 h-full w-full bg-transparent"></div>
        ))}
    </div>
  );
}
